import { CheckCircle2, Flag, Lightbulb, Target } from "lucide-react";

import { SectionLabel } from "./section-label";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

import type { WeekEntry } from "@/routes/_app/-timeline-v2.data";

interface WeekDetailCardProps {
  entry: WeekEntry;
}

export function WeekDetailCard({ entry }: WeekDetailCardProps) {
  const tone: "discovery" | "build" = entry.number <= 2 ? "discovery" : "build";
  return (
    <article data-slot="week-detail" className="glow-card-static p-6">
      <CardHeader entry={entry} tone={tone} />
      <CardBody entry={entry} />
    </article>
  );
}

function CardHeader({ entry, tone }: { entry: WeekEntry; tone: "discovery" | "build" }) {
  return (
    <header className="flex flex-col gap-1 border-b border-[oklch(0.91_0.008_240_/_0.6)] pb-4">
      <HeaderEyebrow number={entry.number} window={entry.window} tone={tone} />
      <h2 className="text-lg font-medium tracking-tight">{entry.title}</h2>
    </header>
  );
}

function HeaderEyebrow({
  number,
  window,
  tone,
}: {
  number: 1 | 2 | 3 | 4;
  window: string;
  tone: "discovery" | "build";
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          "font-data inline-flex h-5 items-center rounded px-1.5 text-[10px] font-semibold tracking-wide",
          tone === "discovery"
            ? "bg-[oklch(0.55_0.17_190_/_0.1)] text-[oklch(0.45_0.18_195)]"
            : "bg-[oklch(0.74_0.16_80_/_0.1)] text-[oklch(0.5_0.18_60)]"
        )}
      >
        Week {number}
      </span>
      <span className="font-data text-muted-foreground text-[10px] tracking-[0.18em] uppercase">
        {window}
      </span>
    </div>
  );
}

function CardBody({ entry }: { entry: WeekEntry }) {
  return (
    <div className="mt-5 flex flex-col gap-5">
      <SectionLabel label="Focus" icon={<Target className="h-3 w-3" />}>
        <p>{entry.focus}</p>
      </SectionLabel>
      <SectionLabel label="Gate" icon={<Flag className="h-3 w-3" />}>
        <p>{entry.gate}</p>
      </SectionLabel>
      <SectionLabel label="Deliverables" icon={<CheckCircle2 className="h-3 w-3" />}>
        <DeliverableList items={entry.deliverables} />
      </SectionLabel>
      {entry.conclusions ? (
        <SectionLabel label="Conclusions" icon={<Lightbulb className="h-3 w-3" />} tone="accent">
          <ConclusionList items={entry.conclusions} />
        </SectionLabel>
      ) : null}
    </div>
  );
}

function DeliverableList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((item) => (
        <DeliverableRow key={item} text={item} />
      ))}
    </ul>
  );
}

function DeliverableRow({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="mt-[3px] h-3.5 w-3.5 shrink-0 text-[oklch(0.66_0.16_155)]" />
      <span>{text}</span>
    </li>
  );
}

function ConclusionList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((item) => (
        <ConclusionRow key={item} text={item} />
      ))}
    </ul>
  );
}

function ConclusionRow({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-[8px] inline-block h-1 w-1 shrink-0 rounded-full bg-[oklch(0.55_0.17_190)]" />
      <span>{text}</span>
    </li>
  );
}
